import { createMutationAtom } from 'atoms/create-resource-atom';
import { atom, Getter, Setter, useAtomValue } from 'jotai';
import { createResourceAtom, createResourceCacheMapAtom } from 'lib/atoms';
import request from 'lib/request';
import { useMemo } from 'react';
import { formatNumber } from '../../utils/number';
import {
  WellRelatedUnitsResponse,
  WellFeaturesResponse,
  WellFormationResponse,
  WellsResponse,
  WellSupportingFileResponse,
  WellVersionsResponse,
  DeclineCurveAnalysis,
  WellboresResponse,
  WellboreGeometryExportResponse,
  WellMapResponse,
  WellRecordVersionsResponse,
  WellDcaScenariosResponse,
  DcaScenariosResponse,
  BulkTypeCurveVolumesResponse,
  EarlyTimeStreamsResponse,
  SaveDcaForecastResponse,
  WellLandingAnalysisRequestCancelResponse,
  WellLandingAnalysisRequestResponse,
  WellLandingAnalysisPreview,
  WellLandingAnalysisResponse,
  WellLandingAnalysisInstancesResponse,
  WellLandingAnalysisRequestStatusResponse,
  BulkForecastMetricData,
  ProductionDecline,
  WellSpacingRequestResponse,
  WellSpacingRequestShowResponse,
  WellSpacingResultsResponse,
  WellSpacingEvalPointResultsResponse,
} from 'types/api-responses';
import { atomFamily, atomWithStorage } from 'jotai/utils';
import {
  DEFAULT_LANDING_ANALYSIS_SETTINGS,
  LandingAnalysisSettingsValues,
  validateInclinationCutoff,
  validateStepFrequency,
  validateMaxThickness,
  ColorByAttribute,
} from 'components/well-3d-viewer/utils';

import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import {
  createForecastSettingsForm,
  createVolumeModelForm,
  currentOrFirstVersion,
  defaultDcaScenario,
  formatList,
  getUnlockedParameters,
} from './utils';
import {
  DcaRegressionApiResponse,
  DcaRegressionResult,
  DcaRegressionStreamResult,
} from 'types/dca-regression-api';
import { MineralType } from 'types/minerals';
import { FailedApiResponse, type ForecastData } from './types';
import { savedProductionVersionId } from './production-version-utils';
import { parseMomentUtc } from 'utils/dates';
import {
  TypeCurveOption,
  TypeCurveOptionsResponse,
} from 'types/early-time-dca';
import {
  AreaConfigResponse,
  DcaScenarioResponse,
  DiFormat,
  DurationOption,
  ProductionStreamConfig,
  QiFormat,
} from 'types/dca-scenarios-api/responses';
import type { ForecastVersion } from './decline-curve-analysis/pdp-forecast-version-selector';
import type { ProductionDataSource } from 'types/api-responses';

export type PdpVersionsRequestParams = {
  uwi: string;
  versions: ForecastVersion[];
  requestId: string;
  declineType?: 'PDP' | 'TC';
} | null;

// Error codes that should be treated as warnings
const warningErrorCodes = ['INFORMATION_MESSAGE', 'WARNING'];

const useWellAtom = (href: string) =>
  useMemo(() => createResourceAtom<WellsResponse>(href), [href]);

const productionForecastsVersionsAtomFamily = atomFamily(
  ({
    href,
    uwi,
    isLegacy,
    includeAllVersions,
    dcaScenarioId,
  }: {
    href: string;
    uwi: string;
    isLegacy: boolean;
    includeAllVersions: boolean;
    dcaScenarioId?: number;
  }) =>
    searchAtomPair<
      WellVersionsResponse,
      SearchParams &
        (
          | {
              // A well may carry the scenario its forecasts are drawn from, which
              // narrows the versions to that scenario's own.
              query: { wells: { uwi: string; dcaScenarioId?: number }[] };
              includeAllVersions?: boolean;
            }
          | { uwis: string[] }
        )
    >(
      { href: href },
      !isLegacy
        ? {
            query: {
              wells: [
                { uwi, ...(dcaScenarioId !== undefined && { dcaScenarioId }) },
              ],
              ...(includeAllVersions && { includeAllVersions: true }),
            },
          }
        : { uwis: [uwi] }
    ).searchAtom,
  (a, b) =>
    a.href === b.href &&
    a.uwi === b.uwi &&
    a.includeAllVersions === b.includeAllVersions &&
    a.dcaScenarioId === b.dcaScenarioId
);

/**
 * Fetch production forecasts versions. Triggers the fetch on mount.
 *
 * @param {string} href - The versions endpoint.
 * @param {string} uwi - The well.
 * @param {boolean} isLegacy - Whether `href` is the legacy PDP versions endpoint.
 * @param {boolean} includeAllVersions - Include unpromoted Insights versions.
 * @param {number} dcaScenarioId - Only list Insights versions from this DCA scenario.
 * @return {object} The search atom for the versions.
 */
const useProductionForecastsVersionsAtom = (
  href: string,
  uwi: string,
  isLegacy: boolean = false,
  includeAllVersions: boolean = false,
  dcaScenarioId?: number
) => ({
  searchAtom: productionForecastsVersionsAtomFamily({
    href,
    uwi,
    isLegacy,
    includeAllVersions,
    dcaScenarioId,
  }),
});

const productionRecordsVersionsAtomFamily = atomFamily(
  ({ href, uwi }: { href: string; uwi: string }) =>
    searchAtomPair<WellVersionsResponse, SearchParams & { uwis: string[] }>(
      { href: href },
      { uwis: [uwi] }
    ).searchAtom,
  (a, b) => a.href === b.href && a.uwi === b.uwi
);

// Fetch production records versions. Triggers the fetch on mount
const useProductionRecordsVersionsAtom = (href: string, uwi: string) => ({
  searchAtom: productionRecordsVersionsAtomFamily({ href, uwi }),
});
const useWellFormationAtom = (href: string) =>
  useMemo(() => createResourceAtom<WellFormationResponse>(href), [href]);

const useWellFeaturesAtom = (href: string) =>
  useMemo(() => createResourceAtom<WellFeaturesResponse>(href), [href]);

const useSupportingFilesAtom = (href: string) =>
  useMemo(() => createResourceAtom<WellSupportingFileResponse[]>(href), [href]);

const useUnitTableAtom = (href: string) =>
  useMemo(() => createResourceAtom<WellRelatedUnitsResponse[]>(href), [href]);

const wellboresMutationAtom = createMutationAtom<
  WellboresResponse,
  'wellbores'
>();

const wellboreGeometryExportMutationAtom = createMutationAtom<
  WellboreGeometryExportResponse,
  'wellboreGeometryExport'
>();

const wellMapMutationAtom = createMutationAtom<WellMapResponse, 'wellMap'>();

const wellSpacingRequestMutationAtom = createMutationAtom<
  WellSpacingRequestResponse,
  'wellSpacingRequest'
>();

const wellSpacingResultsMutationAtom = createMutationAtom<
  WellSpacingResultsResponse,
  'wellSpacingResults'
>();

const wellSpacingEvalPointResultsMutationAtom = createMutationAtom<
  WellSpacingEvalPointResultsResponse,
  'wellSpacingEvalPointResults'
>();

export const useWellSpacingRequestStatusAtom = (href: string | undefined) => {
  return useMemo(
    () => createResourceAtom<WellSpacingRequestShowResponse>(href),
    [href]
  );
};

// Sibling of useWellSpacingRequestStatusAtom for the landing side: GETs the
// request's `/status` (top-level `status` + progress counts) so a subscription
// that missed the terminal Pusher event can recover the run's final state.
export const useWellLandingAnalysisRequestStatusAtom = (
  href: string | undefined
) => {
  return useMemo(
    () => createResourceAtom<WellLandingAnalysisRequestStatusResponse>(href),
    [href]
  );
};

const wellLandingAnalysisRequestMutationAtom = createMutationAtom<
  WellLandingAnalysisRequestResponse,
  'wellLandingAnalysisRequest'
>();

const wellLandingAnalysisPreviewCacheAtom =
  createResourceCacheMapAtom<WellLandingAnalysisPreview>();

const editSourceRefreshCounterAtom = atom(0);

export const triggerEditSourceRefreshAtom = atom(null, (_, set) => {
  set(editSourceRefreshCounterAtom, (c) => c + 1);
});

export const useWellLandingAnalysisPreviewAtom = (href?: string) => {
  const refreshCounter = useAtomValue(editSourceRefreshCounterAtom);
  return useMemo(
    () => createResourceAtom<WellLandingAnalysisPreview>(href),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [href, refreshCounter]
  );
};

const cancelLandingAnalysisRequestMutationAtom = createMutationAtom<
  WellLandingAnalysisRequestCancelResponse,
  'wellLandingAnalysisRequestCancel'
>();

export const wellLandingAnalysisMutationAtom = createMutationAtom<
  WellLandingAnalysisResponse,
  'wellLandingAnalysis'
>();

export const wellLandingAnalysisQueryAtom = createMutationAtom<
  WellLandingAnalysisInstancesResponse,
  'wellLandingAnalysisQuery'
>();

// Separate instance from wellLandingAnalysisQueryAtom so the modal's per-open
// fetches don't affect the one-time initial load state (which drives table loading).
export const wellLandingAnalysisInitialQueryAtom = createMutationAtom<
  WellLandingAnalysisInstancesResponse,
  'wellLandingAnalysisQuery'
>();

// Separate instance for offset wells so each has its own Pusher channel/state.
export const offsetWellLandingAnalysisRequestMutationAtom = createMutationAtom<
  WellLandingAnalysisRequestResponse,
  'wellLandingAnalysisRequest'
>();

// Dedicated query atoms for the Pusher-race recovery backfill (primary + offset),
// kept separate from the bootstrap/modal query atoms so a recovery re-query never
// clobbers their loading state. Each resolves to this run's processed instances;
// the hook `fetchPreview`s every returned href.
export const wellLandingAnalysisRecoveryQueryAtom = createMutationAtom<
  WellLandingAnalysisInstancesResponse,
  'wellLandingAnalysisQuery'
>();

export const offsetWellLandingAnalysisRecoveryQueryAtom = createMutationAtom<
  WellLandingAnalysisInstancesResponse,
  'wellLandingAnalysisQuery'
>();

const savedSubjectUwi14sAtom = atom<string[]>([]);

const colorByAttributeAtom = atom<ColorByAttribute>('well_status');

// Landing analysis: applied (committed) state and draft (form) state
const landingAnalysisSettingsAtom = atom<LandingAnalysisSettingsValues>(
  DEFAULT_LANDING_ANALYSIS_SETTINGS
);
const landingAnalysisDraftSettingsAtom = atom<LandingAnalysisSettingsValues>(
  DEFAULT_LANDING_ANALYSIS_SETTINGS
);

// Derived: dirty when draft differs from applied
const landingSettingsDirtyAtom = atom((get) => {
  const draft = get(landingAnalysisDraftSettingsAtom);
  const applied = get(landingAnalysisSettingsAtom);
  return (
    draft.usePerforations !== applied.usePerforations ||
    draft.inclinationCutoff !== applied.inclinationCutoff ||
    draft.stepFrequency !== applied.stepFrequency ||
    draft.maxThickness !== applied.maxThickness ||
    draft.calculateOffsetWells !== applied.calculateOffsetWells
  );
});

// Derived: form valid based on draft values
const landingSettingsFormValidAtom = atom((get) => {
  const draft = get(landingAnalysisDraftSettingsAtom);
  return (
    validateInclinationCutoff(draft.inclinationCutoff) &&
    validateStepFrequency(draft.stepFrequency) &&
    validateMaxThickness(draft.maxThickness)
  );
});

const wellPerforationFactorMutationAtom = createMutationAtom<
  WellsResponse,
  'wellPerforationFactor'
>();

const wellStickLateralFactorMutationAtom = createMutationAtom<
  WellsResponse,
  'wellStickLateralFactor'
>();

// atom to extract well page versions
const useWellVersionsAtom = (href: string) =>
  useMemo(() => createResourceAtom<Array<WellVersionsResponse>>(href), [href]);

// atom to control the visibility of the well scoped unit layer
const wellScopedUnitLayerVisibility = atomWithStorage(
  'well-scoped-unit-layer',
  true
);
const fallbackAtom = atom(undefined);
const forecastSettingsFormAtom = atom(createForecastSettingsForm());
const _oilModelFormAtom = atom(
  createVolumeModelForm('modified_hyperbolic', 'none', {})
);
const _gasModelFormAtom = atom(
  createVolumeModelForm('modified_hyperbolic', 'none', {})
);
const _waterModelFormAtom = atom(
  createVolumeModelForm('modified_hyperbolic', 'none', {})
);

const oilModelFormAtom = atom(
  (get) => {
    const forecastSettingsForm = get(forecastSettingsFormAtom);
    const oilModelForm = get(_oilModelFormAtom);
    return {
      ...oilModelForm,
      ...forecastSettingsForm,
    };
  },
  (
    get,
    set,
    {
      declineModel,
      rampupModel,
      forecastStartDate,
      qi,
      di,
      bFactor,
      dlim,
      qiRampup,
      rampupDuration,
      n,
      ri,
      tElf,
      m,
    }
  ) => {
    const forecastSettingsForm = get(forecastSettingsFormAtom);
    const diFormat = get(forecastSettingsForm.diFormat);
    set(
      _oilModelFormAtom,
      createVolumeModelForm(declineModel, rampupModel, {
        forecastStartDate,
        qi,
        di,
        bFactor,
        dlim,
        qiRampup,
        rampupDuration,
        n,
        ri,
        tElf,
        m,
        diFormat: diFormat.value,
      })
    );
  }
);

const gasModelFormAtom = atom(
  (get) => {
    const forecastSettingsForm = get(forecastSettingsFormAtom);
    const gasModelForm = get(_gasModelFormAtom);
    return {
      ...gasModelForm,
      ...forecastSettingsForm,
    };
  },
  (
    get,
    set,
    {
      declineModel,
      rampupModel,
      forecastStartDate,
      qi,
      di,
      bFactor,
      dlim,
      qiRampup,
      rampupDuration,
      n,
      ri,
      tElf,
      m,
    }
  ) => {
    const forecastSettingsForm = get(forecastSettingsFormAtom);
    const diFormat = get(forecastSettingsForm.diFormat);
    set(
      _gasModelFormAtom,
      createVolumeModelForm(declineModel, rampupModel, {
        forecastStartDate,
        qi,
        di,
        bFactor,
        dlim,
        qiRampup,
        rampupDuration,
        n,
        ri,
        tElf,
        m,
        diFormat: diFormat.value,
      })
    );
  }
);

const waterModelFormAtom = atom(
  (get) => {
    const forecastSettingsForm = get(forecastSettingsFormAtom);
    const waterModelForm = get(_waterModelFormAtom);
    return {
      ...waterModelForm,
      ...forecastSettingsForm,
    };
  },
  (
    get,
    set,
    {
      declineModel,
      rampupModel,
      forecastStartDate,
      qi,
      di,
      bFactor,
      dlim,
      qiRampup,
      rampupDuration,
      n,
      ri,
      tElf,
      m,
    }
  ) => {
    const forecastSettingsForm = get(forecastSettingsFormAtom);
    const diFormat = get(forecastSettingsForm.diFormat);
    set(
      _waterModelFormAtom,
      createVolumeModelForm(declineModel, rampupModel, {
        forecastStartDate,
        qi,
        di,
        bFactor,
        dlim,
        qiRampup,
        rampupDuration,
        n,
        ri,
        tElf,
        m,
        diFormat: diFormat.value,
      })
    );
  }
);

const oilModelFormValuesAtom = atom((get) => {
  const forecastSettingsForm = get(forecastSettingsFormAtom);
  const oilModelForm = get(oilModelFormAtom);

  const startDateValue = get(oilModelForm.startDateAtomField);
  const qiValue = get(oilModelForm.qiAtomField);
  const diValue = get(oilModelForm.diAtomField);
  const declineModelValue = get(oilModelForm.declineModelAtomField);
  const bFactorValue = get(oilModelForm.bFactorAtomField ?? fallbackAtom);
  const dlimValue = get(oilModelForm.dlimAtomField ?? fallbackAtom);
  const rampupModelValue = get(oilModelForm.rampupModelAtomField);
  const qiRampupValue = get(oilModelForm.qiRampupAtomField ?? fallbackAtom);
  const rampupDurationValue = get(
    oilModelForm.rampupDurationAtomField ?? fallbackAtom
  );
  const nValue = get(oilModelForm.nAtomField ?? fallbackAtom);
  const riValue = get(oilModelForm.riAtomField ?? fallbackAtom);
  const tElfValue = get(oilModelForm.tElfAtomField ?? fallbackAtom);
  const mValue = get(oilModelForm.mAtomField ?? fallbackAtom);
  const forecastDurationValue = get(forecastSettingsForm.forecastDuration);
  const qiFormatValue = get(forecastSettingsForm.qiFormat);
  const diFormatValue = get(forecastSettingsForm.diFormat);
  const forecastDurationOptionValue = get(
    forecastSettingsForm.forecastDurationOption
  );

  return {
    startDate: startDateValue,
    qi: qiValue,
    di: diValue,
    declineModel: declineModelValue,
    bFactor: bFactorValue,
    dlim: dlimValue,
    rampupModel: rampupModelValue,
    qiRampup: qiRampupValue,
    rampupDuration: rampupDurationValue,
    n: nValue,
    ri: riValue,
    tElf: tElfValue,
    m: mValue,
    forecastDuration: forecastDurationValue,
    qiFormat: qiFormatValue,
    diFormat: diFormatValue,
    forecastDurationOption: forecastDurationOptionValue,
  };
});

const gasModelFormValuesAtom = atom((get) => {
  const forecastSettingsForm = get(forecastSettingsFormAtom);
  const gasModelForm = get(gasModelFormAtom);

  const startDateValue = get(gasModelForm.startDateAtomField);
  const qiValue = get(gasModelForm.qiAtomField);
  const diValue = get(gasModelForm.diAtomField);
  const declineModelValue = get(gasModelForm.declineModelAtomField);
  const bFactorValue = get(gasModelForm.bFactorAtomField ?? fallbackAtom);
  const dlimValue = get(gasModelForm.dlimAtomField ?? fallbackAtom);
  const rampupModelValue = get(gasModelForm.rampupModelAtomField);
  const qiRampupValue = get(gasModelForm.qiRampupAtomField ?? fallbackAtom);
  const rampupDurationValue = get(
    gasModelForm.rampupDurationAtomField ?? fallbackAtom
  );
  const nValue = get(gasModelForm.nAtomField ?? fallbackAtom);
  const riValue = get(gasModelForm.riAtomField ?? fallbackAtom);
  const tElfValue = get(gasModelForm.tElfAtomField ?? fallbackAtom);
  const mValue = get(gasModelForm.mAtomField ?? fallbackAtom);
  const forecastDurationValue = get(forecastSettingsForm.forecastDuration);
  const qiFormatValue = get(forecastSettingsForm.qiFormat);
  const diFormatValue = get(forecastSettingsForm.diFormat);
  const forecastDurationOptionValue = get(
    forecastSettingsForm.forecastDurationOption
  );
  return {
    startDate: startDateValue,
    qi: qiValue,
    di: diValue,
    declineModel: declineModelValue,
    bFactor: bFactorValue,
    dlim: dlimValue,
    rampupModel: rampupModelValue,
    qiRampup: qiRampupValue,
    rampupDuration: rampupDurationValue,
    n: nValue,
    ri: riValue,
    tElf: tElfValue,
    m: mValue,
    forecastDuration: forecastDurationValue,
    qiFormat: qiFormatValue,
    diFormat: diFormatValue,
    forecastDurationOption: forecastDurationOptionValue,
  };
});

const waterModelFormValuesAtom = atom((get) => {
  const forecastSettingsForm = get(forecastSettingsFormAtom);
  const waterModelForm = get(waterModelFormAtom);

  const startDateValue = get(waterModelForm.startDateAtomField);
  const qiValue = get(waterModelForm.qiAtomField);
  const diValue = get(waterModelForm.diAtomField);
  const declineModelValue = get(waterModelForm.declineModelAtomField);
  const bFactorValue = get(waterModelForm.bFactorAtomField ?? fallbackAtom);
  const dlimValue = get(waterModelForm.dlimAtomField ?? fallbackAtom);
  const rampupModelValue = get(waterModelForm.rampupModelAtomField);
  const qiRampupValue = get(waterModelForm.qiRampupAtomField ?? fallbackAtom);
  const rampupDurationValue = get(
    waterModelForm.rampupDurationAtomField ?? fallbackAtom
  );
  const nValue = get(waterModelForm.nAtomField ?? fallbackAtom);
  const riValue = get(waterModelForm.riAtomField ?? fallbackAtom);
  const tElfValue = get(waterModelForm.tElfAtomField ?? fallbackAtom);
  const mValue = get(waterModelForm.mAtomField ?? fallbackAtom);
  const forecastDurationValue = get(forecastSettingsForm.forecastDuration);
  const qiFormatValue = get(forecastSettingsForm.qiFormat);
  const diFormatValue = get(forecastSettingsForm.diFormat);
  const forecastDurationOptionValue = get(
    forecastSettingsForm.forecastDurationOption
  );
  return {
    startDate: startDateValue,
    qi: qiValue,
    di: diValue,
    declineModel: declineModelValue,
    bFactor: bFactorValue,
    dlim: dlimValue,
    rampupModel: rampupModelValue,
    qiRampup: qiRampupValue,
    rampupDuration: rampupDurationValue,
    n: nValue,
    ri: riValue,
    tElf: tElfValue,
    m: mValue,
    forecastDuration: forecastDurationValue,
    qiFormat: qiFormatValue,
    diFormat: diFormatValue,
    forecastDurationOption: forecastDurationOptionValue,
  };
});

const isOilModelValidAtom = atom((get) => {
  const oilModelForm = get(oilModelFormValuesAtom);

  const baseFieldsValid =
    oilModelForm.startDate.isValid &&
    oilModelForm.declineModel.isValid &&
    oilModelForm.rampupModel.isValid;

  let conditionalFieldsValid = true;
  if (oilModelForm.declineModel.value !== 'ratio') {
    conditionalFieldsValid =
      conditionalFieldsValid &&
      oilModelForm.qi.isValid &&
      oilModelForm.di.isValid;
  }
  if (oilModelForm.declineModel.value === 'ratio') {
    conditionalFieldsValid =
      conditionalFieldsValid &&
      (oilModelForm?.ri?.isValid ?? false) &&
      (oilModelForm?.tElf?.isValid ?? false) &&
      (oilModelForm?.m?.isValid ?? false);
  }

  if (
    oilModelForm.declineModel.value === 'hyperbolic' ||
    oilModelForm.declineModel.value === 'modified_hyperbolic'
  ) {
    conditionalFieldsValid =
      conditionalFieldsValid && (oilModelForm?.bFactor?.isValid ?? false);
  }

  if (oilModelForm.declineModel.value === 'modified_hyperbolic') {
    conditionalFieldsValid =
      conditionalFieldsValid && (oilModelForm?.dlim?.isValid ?? false);
  }

  if (oilModelForm.rampupModel.value !== 'none') {
    conditionalFieldsValid =
      conditionalFieldsValid &&
      (oilModelForm?.qiRampup?.isValid ?? false) &&
      (oilModelForm?.rampupDuration?.isValid ?? false);
    if (
      oilModelForm.rampupModel.value === 'linear' ||
      oilModelForm.rampupModel.value === 'exponential'
    ) {
      conditionalFieldsValid =
        conditionalFieldsValid && (oilModelForm?.n?.isValid ?? false);
    }
  }
  return baseFieldsValid && conditionalFieldsValid;
});

const isGasModelValidAtom = atom((get) => {
  const gasModelForm = get(gasModelFormValuesAtom);

  const baseFieldsValid =
    gasModelForm.startDate.isValid &&
    gasModelForm.declineModel.isValid &&
    gasModelForm.rampupModel.isValid;

  let conditionalFieldsValid = true;
  if (gasModelForm.declineModel.value !== 'ratio') {
    conditionalFieldsValid =
      conditionalFieldsValid &&
      gasModelForm.qi.isValid &&
      gasModelForm.di.isValid;
  }
  if (gasModelForm.declineModel.value === 'ratio') {
    conditionalFieldsValid =
      conditionalFieldsValid &&
      (gasModelForm?.ri?.isValid ?? false) &&
      (gasModelForm?.tElf?.isValid ?? false) &&
      (gasModelForm?.m?.isValid ?? false);
  }
  if (gasModelForm.rampupModel.value !== 'none') {
    conditionalFieldsValid =
      conditionalFieldsValid &&
      (gasModelForm?.qiRampup?.isValid ?? false) &&
      (gasModelForm?.rampupDuration?.isValid ?? false);
    if (
      gasModelForm.rampupModel.value === 'linear' ||
      gasModelForm.rampupModel.value === 'exponential'
    ) {
      conditionalFieldsValid =
        conditionalFieldsValid && (gasModelForm?.n?.isValid ?? false);
    }
  }

  if (
    gasModelForm.declineModel.value === 'hyperbolic' ||
    gasModelForm.declineModel.value === 'modified_hyperbolic'
  ) {
    conditionalFieldsValid =
      conditionalFieldsValid && (gasModelForm?.bFactor?.isValid ?? false);
  }

  if (gasModelForm.declineModel.value === 'modified_hyperbolic') {
    conditionalFieldsValid =
      conditionalFieldsValid && (gasModelForm?.dlim?.isValid ?? false);
  }

  return baseFieldsValid && conditionalFieldsValid;
});

const isWaterModelValidAtom = atom((get) => {
  const waterModelForm = get(waterModelFormValuesAtom);

  const baseFieldsValid =
    waterModelForm.startDate.isValid &&
    waterModelForm.declineModel.isValid &&
    waterModelForm.rampupModel.isValid;

  let conditionalFieldsValid = true;
  if (waterModelForm.declineModel.value !== 'ratio') {
    conditionalFieldsValid =
      conditionalFieldsValid &&
      waterModelForm.qi.isValid &&
      waterModelForm.di.isValid;
  }
  if (waterModelForm.declineModel.value === 'ratio') {
    conditionalFieldsValid =
      conditionalFieldsValid &&
      (waterModelForm?.ri?.isValid ?? false) &&
      (waterModelForm?.tElf?.isValid ?? false) &&
      (waterModelForm?.m?.isValid ?? false);
  }
  if (
    waterModelForm.declineModel.value === 'hyperbolic' ||
    waterModelForm.declineModel.value === 'modified_hyperbolic'
  ) {
    conditionalFieldsValid =
      conditionalFieldsValid && (waterModelForm?.bFactor?.isValid ?? false);
  }
  if (waterModelForm.declineModel.value === 'modified_hyperbolic') {
    conditionalFieldsValid =
      conditionalFieldsValid && (waterModelForm?.dlim?.isValid ?? false);
  }
  if (waterModelForm.rampupModel.value !== 'none') {
    conditionalFieldsValid =
      conditionalFieldsValid &&
      (waterModelForm?.qiRampup?.isValid ?? false) &&
      (waterModelForm?.rampupDuration?.isValid ?? false);
    if (
      waterModelForm.rampupModel.value === 'linear' ||
      waterModelForm.rampupModel.value === 'exponential'
    ) {
      conditionalFieldsValid =
        conditionalFieldsValid && (waterModelForm?.n?.isValid ?? false);
    }
  }

  return baseFieldsValid && conditionalFieldsValid;
});

// Helper function to check if a model form is dirty
const checkModelFormDirty = (
  modelFormValues: ReturnType<
    | (typeof oilModelFormValuesAtom)['read']
    | (typeof gasModelFormValuesAtom)['read']
    | (typeof waterModelFormValuesAtom)['read']
  >
) => {
  // Check base fields
  const baseFieldsDirty =
    modelFormValues.startDate.isDirty ||
    modelFormValues.declineModel.isDirty ||
    modelFormValues.rampupModel.isDirty ||
    modelFormValues.forecastDuration.isDirty ||
    modelFormValues.qiFormat.isDirty ||
    modelFormValues.diFormat.isDirty ||
    modelFormValues.forecastDurationOption.isDirty;

  // Check conditional fields that may exist
  const conditionalFieldsDirty =
    (modelFormValues?.qi?.isDirty ?? false) ||
    (modelFormValues?.di?.isDirty ?? false) ||
    (modelFormValues?.bFactor?.isDirty ?? false) ||
    (modelFormValues?.dlim?.isDirty ?? false) ||
    (modelFormValues?.qiRampup?.isDirty ?? false) ||
    (modelFormValues?.rampupDuration?.isDirty ?? false) ||
    (modelFormValues?.n?.isDirty ?? false) ||
    (modelFormValues?.ri?.isDirty ?? false) ||
    (modelFormValues?.tElf?.isDirty ?? false) ||
    (modelFormValues?.m?.isDirty ?? false);

  return baseFieldsDirty || conditionalFieldsDirty;
};

// Single atom to check if a model form is dirty, takes model type as parameter
const isModelFormDirtyAtom = atom(
  (get) => (modelType: 'oil' | 'gas' | 'water') => {
    const modelFormValuesAtomMap = {
      oil: oilModelFormValuesAtom,
      gas: gasModelFormValuesAtom,
      water: waterModelFormValuesAtom,
    };

    const modelFormValues = get(modelFormValuesAtomMap[modelType]);
    return checkModelFormDirty(modelFormValues);
  }
);

// Atom to reset dirty state for all model forms
const resetModelFormDirtyAtom = atom(
  null,
  (get, set, modelTypes?: ('oil' | 'gas' | 'water')[]) => {
    const modelsToReset = modelTypes ?? ['oil', 'gas', 'water'];

    modelsToReset.forEach((modelType) => {
      const modelFormAtomMap = {
        oil: oilModelFormAtom,
        gas: gasModelFormAtom,
        water: waterModelFormAtom,
      };

      const modelFormAtom = modelFormAtomMap[modelType];
      const currentForm = get(modelFormAtom);

      // Reset each field atom individually with skipDirtyCheck option
      // Base fields that always exist
      set(currentForm.declineModelAtomField, {
        value: get(currentForm.declineModelAtomField).value,
        __options: { skipDirtyCheck: true },
      });
      set(currentForm.rampupModelAtomField, {
        value: get(currentForm.rampupModelAtomField).value,
        __options: { skipDirtyCheck: true },
      });
      set(currentForm.startDateAtomField, {
        value: get(currentForm.startDateAtomField).value,
        __options: { skipDirtyCheck: true },
      });
      set(currentForm.qiAtomField, {
        value: get(currentForm.qiAtomField).value,
        __options: { skipDirtyCheck: true },
      });
      set(currentForm.diAtomField, {
        value: get(currentForm.diAtomField).value,
        __options: { skipDirtyCheck: true },
      });

      // Conditional fields that may or may not exist based on decline/rampup model
      if (currentForm.bFactorAtomField) {
        set(currentForm.bFactorAtomField, {
          value: get(currentForm.bFactorAtomField).value,
          __options: { skipDirtyCheck: true },
        });
      }
      if (currentForm.dlimAtomField) {
        set(currentForm.dlimAtomField, {
          value: get(currentForm.dlimAtomField).value,
          __options: { skipDirtyCheck: true },
        });
      }
      if (currentForm.qiRampupAtomField) {
        set(currentForm.qiRampupAtomField, {
          value: get(currentForm.qiRampupAtomField).value,
          __options: { skipDirtyCheck: true },
        });
      }
      if (currentForm.rampupDurationAtomField) {
        set(currentForm.rampupDurationAtomField, {
          value: get(currentForm.rampupDurationAtomField).value,
          __options: { skipDirtyCheck: true },
        });
      }
      if (currentForm.nAtomField) {
        set(currentForm.nAtomField, {
          value: get(currentForm.nAtomField).value,
          __options: { skipDirtyCheck: true },
        });
      }
      if (currentForm.riAtomField) {
        set(currentForm.riAtomField, {
          value: get(currentForm.riAtomField).value,
          __options: { skipDirtyCheck: true },
        });
      }
      if (currentForm.tElfAtomField) {
        set(currentForm.tElfAtomField, {
          value: get(currentForm.tElfAtomField).value,
          __options: { skipDirtyCheck: true },
        });
      }
      if (currentForm.mAtomField) {
        set(currentForm.mAtomField, {
          value: get(currentForm.mAtomField).value,
          __options: { skipDirtyCheck: true },
        });
      }

      // Also reset forecast settings fields
      const forecastSettingsForm = get(forecastSettingsFormAtom);
      const forecastDuration = get(forecastSettingsForm.forecastDuration);
      const qiFormat = get(forecastSettingsForm.qiFormat);
      const diFormat = get(forecastSettingsForm.diFormat);
      const forecastDurationOption = get(
        forecastSettingsForm.forecastDurationOption
      );

      set(forecastSettingsForm.forecastDuration, {
        value: forecastDuration.value,
        __options: { skipDirtyCheck: true },
      });
      set(forecastSettingsForm.qiFormat, {
        value: qiFormat.value,
        __options: { skipDirtyCheck: true },
      });
      set(forecastSettingsForm.diFormat, {
        value: diFormat.value,
        __options: { skipDirtyCheck: true },
      });
      set(forecastSettingsForm.forecastDurationOption, {
        value: forecastDurationOption.value,
        __options: { skipDirtyCheck: true },
      });
    });
  }
);

// Type for initial model values
interface InitialModelValues {
  declineModel: any;
  rampupModel: any;
  forecastStartDate: Date | null;
  qi: number | null;
  di: number | null;
  bFactor: number | null;
  dlim: number | null;
  qiRampup: number | null;
  rampupDuration: number | null;
  n: number | null;
  ri: number | null;
  tElf: number | null;
  m: number | null;
}

// Store initial values for each model form
const initialOilModelValuesAtom = atom<InitialModelValues | null>(null);
const initialGasModelValuesAtom = atom<InitialModelValues | null>(null);
const initialWaterModelValuesAtom = atom<InitialModelValues | null>(null);

// Consolidated reset function that takes a model type
const resetModelFormAtom = atom(
  null,
  (get, set, modelType: 'oil' | 'gas' | 'water') => {
    const baseFormAtomMap = {
      oil: oilModelFormAtom,
      gas: gasModelFormAtom,
      water: waterModelFormAtom,
    };

    const initialValuesAtomMap = {
      oil: initialOilModelValuesAtom,
      gas: initialGasModelValuesAtom,
      water: initialWaterModelValuesAtom,
    };

    const baseFormAtom = baseFormAtomMap[modelType];
    const initialValuesAtom = initialValuesAtomMap[modelType];
    const initialValues = get(initialValuesAtom);

    if (!initialValues) return;

    const resetValues = {
      declineModel: initialValues.declineModel || 'modified_hyperbolic',
      rampupModel: initialValues.rampupModel || 'none',
      forecastStartDate: initialValues.forecastStartDate || null,
      qi: initialValues.qi ?? null,
      di: initialValues.di ?? null,
      bFactor: initialValues.bFactor ?? null,
      dlim: initialValues.dlim ?? null,
      qiRampup: initialValues.qiRampup ?? null,
      rampupDuration: initialValues.rampupDuration ?? null,
      n: initialValues.n ?? null,
      ri: initialValues.ri ?? null,
      tElf: initialValues.tElf ?? null,
      m: initialValues.m ?? null,
    };

    // The other streams may still hold a fit's parameters, so the fitted
    // production stays; the panel's reset of every stream clears it.
    set(baseFormAtom, resetValues);
  }
);

const resetOilModelFormAtom = atom(null, (get, set) => {
  set(resetModelFormAtom, 'oil');
});

const resetGasModelFormAtom = atom(null, (get, set) => {
  set(resetModelFormAtom, 'gas');
});

const resetWaterModelFormAtom = atom(null, (get, set) => {
  set(resetModelFormAtom, 'water');
});

const resetForecastSettingsFormAtom = atom(null, (get, set) => {
  const forecastDuration = get(forecastSettingsFormAtom).forecastDuration;
  const qiFormat = get(forecastSettingsFormAtom).qiFormat;
  const diFormat = get(forecastSettingsFormAtom).diFormat;
  const forecastDurationOption = get(
    forecastSettingsFormAtom
  ).forecastDurationOption;
  set(forecastDuration, { value: 30, __options: { skipDirtyCheck: true } });
  set(qiFormat, { value: 'months', __options: { skipDirtyCheck: true } });
  set(diFormat, { value: 'effective', __options: { skipDirtyCheck: true } });
  set(forecastDurationOption, {
    value: 'forecast_start',
    __options: { skipDirtyCheck: true },
  });
});

const primaryDCAModelAtom = atom((get) => {
  const isOilModelValid = get(isOilModelValidAtom);
  const isGasModelValid = get(isGasModelValidAtom);
  const oilModelFormValues = get(oilModelFormValuesAtom);
  const gasModelFormValues = get(gasModelFormValuesAtom);
  return isOilModelValid && oilModelFormValues.declineModel.value !== 'ratio'
    ? { streamType: 'oil' as const, formValues: oilModelFormValues }
    : isGasModelValid && gasModelFormValues.declineModel.value !== 'ratio'
      ? { streamType: 'gas' as const, formValues: gasModelFormValues }
      : null;
});

const isPrimaryDCAModelValidAtom = atom((get) => {
  const primaryDCAModel = get(primaryDCAModelAtom);
  if (!primaryDCAModel) return false;
  return get(
    {
      oil: isOilModelValidAtom,
      gas: isGasModelValidAtom,
      water: isWaterModelValidAtom,
    }[primaryDCAModel.streamType]
  );
});

const isMainDCAModelNonExponentialAtom = atom((get) => {
  const primaryDCAModel = get(primaryDCAModelAtom);
  return !!(primaryDCAModel?.formValues.declineModel.value !== 'exponential');
});

const convertToHyperbolicErrorAtom = atom<
  DeclineCurveAnalysis['pdpDeclines'][string][0]['errors']
>([]);

export interface LockedParameters {
  oil: {
    dca: Record<string, number | string>;
    rampUp: Record<string, number | string>;
  };
  gas: {
    dca: Record<string, number | string>;
    rampUp: Record<string, number | string>;
  };
  water: {
    dca: Record<string, number | string>;
    rampUp: Record<string, number | string>;
  };
}

type LockParamsActions =
  | {
      action: 'lock';
      stream: 'oil' | 'gas' | 'water';
      type: 'dca' | 'rampUp';
      parameter: string;
      value: number | string;
    }
  | {
      action: 'unlock';
      stream: 'oil' | 'gas' | 'water';
      type: 'dca' | 'rampUp';
      parameter: string;
    }
  | {
      action: 'reset';
      stream: 'oil' | 'gas' | 'water';
      type: 'dca' | 'rampUp';
    };

const initialLockedParams = {
  oil: {
    dca: {},
    rampUp: {},
  },
  gas: {
    dca: {},
    rampUp: {},
  },
  water: {
    dca: {},
    rampUp: {},
  },
};

export const _lockParamsStateAtom = atom<LockedParameters>(initialLockedParams);

const lockParamsAtom = atom(
  (get) => get(_lockParamsStateAtom),
  (get, set, params: LockParamsActions) => {
    const current = get(_lockParamsStateAtom);

    const updated = {
      oil: {
        dca: { ...current.oil.dca },
        rampUp: { ...current.oil.rampUp },
      },
      gas: {
        dca: { ...current.gas.dca },
        rampUp: { ...current.gas.rampUp },
      },
      water: {
        dca: { ...current.water.dca },
        rampUp: { ...current.water.rampUp },
      },
    };

    if (params.action === 'lock') {
      updated[params.stream][params.type][params.parameter] = params.value;
    }

    if (params.action === 'unlock') {
      delete updated[params.stream][params.type][params.parameter];
    }

    if (params.action === 'reset') {
      updated[params.stream][params.type] = {};
    }

    set(_lockParamsStateAtom, updated);
  }
);

type NotificationKind =
  'error' | 'info' | 'info-square' | 'success' | 'warning' | 'warning-alt';

interface DcaNotification {
  id: string;
  message: string;
  title: string;
  type: NotificationKind;
  hideCloseButton?: boolean;
  timestamp?: number;
}

type DcaNotificationState = Record<string, DcaNotification[]>;

const dcaNotificationAtom = atom<DcaNotificationState>({});

const addDcaNotificationAtom = atom(
  null,
  (get, set, groupId: string, notifications: DcaNotification[]) => {
    const current = get(dcaNotificationAtom);
    const existingGroupNotifications = current[groupId] || [];

    const existingIds = new Set(existingGroupNotifications.map((n) => n.id));

    const newNotifications = notifications
      .filter((n) => !existingIds.has(n.id))
      .map((n) => ({ ...n, timestamp: n.timestamp || Date.now() }));

    if (newNotifications.length === 0) return;

    const updatedGroupNotifications = [
      ...existingGroupNotifications,
      ...newNotifications,
    ];

    set(dcaNotificationAtom, {
      ...current,
      [groupId]: updatedGroupNotifications,
    });
  }
);

const setDcaNotificationAtom = atom(
  null,
  (get, set, groupId: string, notifications: DcaNotification[]) => {
    const current = get(dcaNotificationAtom);
    const notificationsWithTimestamp = notifications.map((n) => ({
      ...n,
      timestamp: n.timestamp || Date.now(),
    }));

    set(dcaNotificationAtom, {
      ...current,
      [groupId]: notificationsWithTimestamp,
    });
  }
);

const clearDcaNotificationGroupAtom = atom(
  null,
  (get, set, groupId: string) => {
    const current = get(dcaNotificationAtom);
    if (!current[groupId]) return;

    const { [groupId]: _, ...rest } = current;
    set(dcaNotificationAtom, rest);
  }
);

const clearAllDcaNotificationsAtom = atom(null, (_, set) => {
  set(dcaNotificationAtom, {});
});

interface RegressionData {
  loading: boolean;
  error: string | null;
  warning: string | null;
  data: DcaRegressionApiResponse | null;
  refetchEvalVolumes: boolean;
}

const regressionDataAtom = atom<RegressionData>({
  loading: false,
  error: null,
  warning: null,
  data: null,
  refetchEvalVolumes: false,
});

const segmentationAndOutlierMarkersBaseAtom = atom<{
  segmentation: Record<MineralType, string[]>;
  outlier: Record<MineralType, string[]>;
} | null>(null);

const segmentationAndOutlierMarkersAtom = atom(
  (get) => get(segmentationAndOutlierMarkersBaseAtom),
  (get, set, value: DcaRegressionApiResponse['bulkDcaRegressionResults']) => {
    const markersData: {
      segmentation: Record<MineralType, string[]>;
      outlier: Record<MineralType, string[]>;
    } = {
      segmentation: { oil: [], gas: [], water: [] },
      outlier: { oil: [], gas: [], water: [] },
    };

    if (value) {
      value.forEach((result: DcaRegressionResult) => {
        if (result?.results) {
          Object.entries(result.results).forEach(
            ([stream, streamResult]: [string, any]) => {
              if (streamResult?.segmentStartDates) {
                // merge, avoid duplicates
                const prev =
                  markersData.segmentation[stream as MineralType] || [];
                markersData.segmentation[stream as MineralType] = Array.from(
                  new Set(prev.concat(streamResult.segmentStartDates))
                );
              }
              if (streamResult?.outlierDates) {
                const prev = markersData.outlier[stream as MineralType] || [];
                markersData.outlier[stream as MineralType] = Array.from(
                  new Set(prev.concat(streamResult.outlierDates))
                );
              }
            }
          );
        }
      });
    }

    set(segmentationAndOutlierMarkersBaseAtom, {
      segmentation: markersData.segmentation,
      outlier: markersData.outlier,
    });
  }
);

export interface RegressionWellParams {
  uwi: string;
  versionId: string;
  dcaScenarioId: number;
  oilModelFormAtom: typeof oilModelFormAtom;
  gasModelFormAtom: typeof gasModelFormAtom;
  waterModelFormAtom: typeof waterModelFormAtom;
  regressStreams: MineralType[];
  lockedParams: LockedParameters;
  typeCurveId: string | null;
  uwi12: string;
  perforatedLateralLengthFt: number | null;
}

const updateModelFormWithResults = (
  get: Getter,
  set: Setter,
  streamResults: DcaRegressionStreamResult,
  modelFormAtom:
    | typeof oilModelFormAtom
    | typeof gasModelFormAtom
    | typeof waterModelFormAtom
) => {
  const currentForm = get(modelFormAtom);
  const { rampUp, ...optimizedParameters } =
    streamResults.optimizedParameters || {};

  const isRampUpNull = !rampUp || Object.keys(rampUp).length === 0;

  const formatDecimal = (value: number) => {
    return Number(
      formatNumber({
        value,
        minPrecision: 0,
        maxPrecision: 2,
        includeCommas: false,
      })
    );
  };

  const newValues = {
    declineModel: get(currentForm.declineModelAtomField).value,
    rampupModel: isRampUpNull
      ? 'none'
      : get(currentForm.rampupModelAtomField).value,
    forecastStartDate: streamResults.forecastStartDate
      ? parseMomentUtc(streamResults.forecastStartDate).toDate()
      : get(currentForm.startDateAtomField).value,
    qi: optimizedParameters.qi
      ? Math.round(optimizedParameters.qi)
      : get(currentForm.qiAtomField).value,
    di: optimizedParameters.di
      ? formatDecimal(optimizedParameters.di)
      : get(currentForm.diAtomField).value,
    bFactor: optimizedParameters.bFactor
      ? formatDecimal(optimizedParameters.bFactor)
      : currentForm.bFactorAtomField
        ? get(currentForm.bFactorAtomField).value
        : null,
    dlim: optimizedParameters.dlim
      ? formatDecimal(optimizedParameters.dlim)
      : currentForm.dlimAtomField
        ? get(currentForm.dlimAtomField).value
        : null,
    qiRampup: isRampUpNull
      ? null
      : rampUp?.qi
        ? Math.round(rampUp.qi)
        : currentForm.qiRampupAtomField
          ? get(currentForm.qiRampupAtomField).value
          : null,
    rampupDuration: isRampUpNull
      ? null
      : (rampUp?.tRamp ??
        (currentForm.rampupDurationAtomField
          ? get(currentForm.rampupDurationAtomField).value
          : null)),
    n: isRampUpNull
      ? null
      : rampUp?.n
        ? formatDecimal(rampUp.n)
        : currentForm.nAtomField
          ? get(currentForm.nAtomField).value
          : null,
    ri: optimizedParameters.ri
      ? formatDecimal(optimizedParameters.ri)
      : currentForm.riAtomField
        ? get(currentForm.riAtomField).value
        : null,
    tElf: optimizedParameters.tElf
      ? formatDecimal(optimizedParameters.tElf)
      : currentForm.tElfAtomField
        ? get(currentForm.tElfAtomField).value
        : null,
    m: optimizedParameters.m
      ? formatDecimal(optimizedParameters.m)
      : currentForm.mAtomField
        ? get(currentForm.mAtomField).value
        : null,
  };

  set(modelFormAtom, newValues);
};

const fetchRegressionAtom = atom(
  (get) => get(regressionDataAtom),
  async (get, set, params: { wells: RegressionWellParams[] }) => {
    const { wells } = params;

    const forecastSettings = get(forecastSettingsFormAtom);

    const { uwis, versionIds } = wells.reduce<{
      uwis: string[];
      versionIds: string[];
    }>(
      (acc, well) => {
        acc.uwis = [...acc.uwis, well.uwi];
        acc.versionIds = [...acc.versionIds, well.versionId];
        return acc;
      },
      { uwis: [], versionIds: [] }
    );

    const createStreamDcaParams = (
      streamType: MineralType,
      modelFormValues:
        | ReturnType<(typeof oilModelFormAtom)['read']>
        | ReturnType<(typeof gasModelFormAtom)['read']>
        | ReturnType<(typeof waterModelFormAtom)['read']>,
      lockedParams: LockedParameters[typeof streamType],
      unlockedParams: { dca: string[]; rampUp: string[] },
      mainStream?: 'oil' | 'gas'
    ) => {
      const dcaModel = get(modelFormValues.declineModelAtomField).value;
      const ramUpModel = get(modelFormValues.rampupModelAtomField).value;

      return {
        dcaModel: dcaModel,
        qiFormat: get(modelFormValues.qiFormat).value,
        durationPeriod: get(modelFormValues.forecastDuration).value,
        durationFormat: 'years' as const,
        lockedParameters: lockedParams.dca,
        unlockedParameters: unlockedParams.dca,

        ...(ramUpModel !== 'none' &&
          dcaModel !== 'ratio' && {
            rampUp: {
              model: ramUpModel,
              lockedParameters: lockedParams.rampUp,
              unlockedParameters: unlockedParams.rampUp || [],
            },
          }),
        ...(dcaModel === 'ratio' && mainStream && { mainStream }),
      };
    };

    // The production this fit reads, kept for Save once the fit succeeds.
    const fittedSource = get(selectedProductionSourceAtom);
    const fittedVersion = get(selectedRecordVersionAtom);
    const fittedProduction: FittedProduction = {
      source: fittedSource,
      productionVolumeVersionId: fittedVersion
        ? savedProductionVersionId(fittedSource, fittedVersion, new Date())
        : null,
    };

    const payload = {
      dcaRegression: {
        // The source the editor shows, so the fit is on the production on screen
        // rather than the one the scenario's hierarchy would pick.
        productionSource: fittedProduction.source,
        productionVolumes: [{ uwis, versionIds }],
        bulkDcaRegression: wells.map((well) => {
          const oilModelFormValues = get(well.oilModelFormAtom);
          const gasModelFormValues = get(well.gasModelFormAtom);
          const waterModelFormValues = get(well.waterModelFormAtom);
          const lockedParams = well.lockedParams;

          const oilDcaModel = get(
            oilModelFormValues.declineModelAtomField
          ).value;
          const gasDcaModel = get(
            gasModelFormValues.declineModelAtomField
          ).value;

          const mainStreamDependencyModels = [
            'hyperbolic',
            'modified_hyperbolic',
          ];
          const mainSteam = mainStreamDependencyModels.includes(oilDcaModel)
            ? 'oil'
            : mainStreamDependencyModels.includes(gasDcaModel)
              ? 'gas'
              : undefined;

          const modelSpec = {
            oil: {
              dca: get(oilModelFormValues.declineModelAtomField).value,
              rampUp: get(oilModelFormValues.rampupModelAtomField).value,
            },
            gas: {
              dca: get(gasModelFormValues.declineModelAtomField).value,
              rampUp: get(gasModelFormValues.rampupModelAtomField).value,
            },
            water: {
              dca: get(waterModelFormValues.declineModelAtomField).value,
              rampUp: get(waterModelFormValues.rampupModelAtomField).value,
            },
          };

          const unlockedParameters = getUnlockedParameters(
            lockedParams,
            modelSpec
          );

          const dcaParams: any = {
            diFormat: get(forecastSettings.diFormat).value,
            forecastDurationOption: get(forecastSettings.forecastDurationOption)
              .value,
          };

          if (well.regressStreams.includes('oil')) {
            dcaParams.oilDcaParams = createStreamDcaParams(
              'oil',
              oilModelFormValues,
              lockedParams.oil,
              unlockedParameters.oil,
              mainSteam
            );
          }

          if (well.regressStreams.includes('gas')) {
            dcaParams.gasDcaParams = createStreamDcaParams(
              'gas',
              gasModelFormValues,
              lockedParams.gas,
              unlockedParameters.gas,
              mainSteam
            );
          }

          if (well.regressStreams.includes('water')) {
            dcaParams.waterDcaParams = createStreamDcaParams(
              'water',
              waterModelFormValues,
              lockedParams.water,
              unlockedParameters.water,
              mainSteam
            );
          }

          return {
            uwi: well.uwi,
            dcaScenarioId: well.dcaScenarioId,
            uniqueTcId: well.typeCurveId,
            dcaParams: dcaParams,
            uwi12: well.uwi12,
            perforatedLateralLengthFt: well.perforatedLateralLengthFt,
          };
        }),
      },
    };

    set(regressionDataAtom, {
      ...get(regressionDataAtom),
      loading: true,
      error: null,
      data: null,
    });

    try {
      const response = await request.post<
        DcaRegressionApiResponse,
        FailedApiResponse
      >('/reservoir/dca_regressions', payload, new AbortController());

      if (response.type === 'success') {
        let msg = '';
        let warning = '';

        wells.forEach((well) => {
          const wellResult = response.data.bulkDcaRegressionResults.find(
            (result) => result.uwi === well.uwi
          );

          if (
            wellResult &&
            (wellResult.status === 'success' ||
              wellResult.status === 'partial_success') &&
            wellResult.results
          ) {
            const { results } = wellResult;

            let refetch = false;

            const earlyTimeRegression: { rampup: string[]; decline: string[] } =
              {
                rampup: [],
                decline: [],
              };

            set(clearDcaNotificationGroupAtom, 'early-time-regression');

            // Update oil model form if oil results exist
            if (results.oil) {
              updateModelFormWithResults(
                get,
                set,
                results.oil,
                well.oilModelFormAtom
              );

              const isOilFormValid = get(isOilModelValidAtom);
              refetch = isOilFormValid;

              if ('inRampUp' in results.oil) {
                if (results.oil.inRampUp === true) {
                  earlyTimeRegression.rampup.push('OIL');
                } else if (results.oil.inRampUp === false) {
                  earlyTimeRegression.decline.push('OIL');
                }
              }
            }

            // Update gas model form if gas results exist
            if (results.gas) {
              updateModelFormWithResults(
                get,
                set,
                results.gas,
                well.gasModelFormAtom
              );
              const isGasFormValid = get(isGasModelValidAtom);
              refetch = isGasFormValid;

              if ('inRampUp' in results.gas) {
                if (results.gas.inRampUp === true) {
                  earlyTimeRegression.rampup.push('GAS');
                } else if (results.gas.inRampUp === false) {
                  earlyTimeRegression.decline.push('GAS');
                }
              }
            }

            // Update water model form if water results exist
            if (results.water) {
              updateModelFormWithResults(
                get,
                set,
                results.water,
                well.waterModelFormAtom
              );

              const isWaterFormValid = get(isWaterModelValidAtom);
              refetch = isWaterFormValid;

              if ('inRampUp' in results.water) {
                if (results.water.inRampUp === true) {
                  earlyTimeRegression.rampup.push('WATER');
                } else if (results.water.inRampUp === false) {
                  earlyTimeRegression.decline.push('WATER');
                }
              }
            }

            const errors =
              wellResult?.errors.length > 0 ? wellResult?.errors : [];

            if (errors) {
              const warningErrorCodesList = errors.filter((error) =>
                warningErrorCodes.includes(error.code)
              );

              if (warningErrorCodesList.length > 0) {
                warning = warningErrorCodesList
                  .map((error) => `${error.message}`)
                  .join('\n');
              }

              wellResult?.errors
                .filter((error) => !warningErrorCodes.includes(error.code))
                .forEach((error) => {
                  msg += `${error.message}`;
                  if (error.details && typeof error.details === 'object') {
                    msg += ` | ${Object.entries(error.details)
                      .map(([key, value]) => `${key}: ${value}`)
                      .join(', ')}\n`;
                  }
                });
            }

            const earlyTimeRegressionMessages: string[] = [];

            if (earlyTimeRegression.rampup.length > 0) {
              const earlyTimeRampup = formatList(earlyTimeRegression.rampup, {
                conjunction: 'and',
                separator: ',',
                verbs: ['is', 'are'],
              });

              earlyTimeRegressionMessages.push(
                `${earlyTimeRampup} in Ramp-up Period`
              );
            }

            if (earlyTimeRegression.decline.length > 0) {
              const earlyTimeDecline = formatList(earlyTimeRegression.decline, {
                conjunction: 'and',
                separator: ',',
                verbs: ['is', 'are'],
              });

              earlyTimeRegressionMessages.push(
                `${earlyTimeDecline} in Decline Period`
              );
            }

            if (earlyTimeRegressionMessages.length > 0) {
              set(addDcaNotificationAtom, 'early-time-regression', [
                {
                  id: 'early-time-regression',
                  title: 'Early Time DCA',
                  message: `${earlyTimeRegressionMessages.join(
                    ', '
                  )}, Type Curve used: ${well.typeCurveId}`,
                  type: 'info',
                },
              ]);
            }

            set(regressionDataAtom, {
              loading: false,
              error: msg || (!refetch ? 'Invalid model form values' : null),
              warning: warning || null,
              data: response.data,
              refetchEvalVolumes: refetch,
            });
            set(fittedProductionAtom, fittedProduction);

            set(segmentationAndOutlierMarkersAtom, [wellResult]);
          } else {
            const errors =
              wellResult?.errors && wellResult?.errors.length > 0
                ? wellResult?.errors
                : [];

            if (errors) {
              const warningErrorCodesList = errors.filter((error) =>
                warningErrorCodes.includes(error.code)
              );

              if (warningErrorCodesList.length > 0) {
                warning = warningErrorCodesList
                  .map((error) => `${error.message}`)
                  .join('\n');
              }
              wellResult?.errors
                .filter((error) => !warningErrorCodes.includes(error.code))
                .forEach((error) => {
                  msg += `${error.message}`;
                  if (error.details && typeof error.details === 'object') {
                    msg += ` | ${Object.entries(error.details)
                      .map(([key, value]) => `${key}: ${value}`)
                      .join(', ')}\n`;
                  }
                });
            }

            set(regressionDataAtom, {
              ...get(regressionDataAtom),
              loading: false,
              error: msg || null,
              warning: warning || null,
              data: null,
            });
          }
        });
      } else if (response.type === 'error') {
        const error = response.data?.errors[0];

        const { details } = error || {};

        let msg = error?.message || '';

        if (details && typeof details === 'object') {
          // Convert details object into a readable string
          msg = Object.entries(details)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n');
        }

        set(regressionDataAtom, {
          ...get(regressionDataAtom),
          loading: false,
          warning: null,
          error: msg,
          data: null,
        });
      } else {
        set(regressionDataAtom, {
          ...get(regressionDataAtom),
          loading: false,
          warning: null,
          error: response.message || 'Failed to fetch regression',
          data: null,
        });
      }
    } catch (error) {
      // Handle unexpected errors
      set(regressionDataAtom, {
        ...get(regressionDataAtom),
        loading: false,
        warning: null,
        error:
          error instanceof Error ? error.message : 'Failed to fetch regression',
        data: null,
      });
    }
  }
);

const resetRegressionDataAtom = atom(null, (get, set) => {
  set(regressionDataAtom, {
    ...get(regressionDataAtom),
    loading: false,
    error: null,
    warning: null,
    data: null,
  });

  set(segmentationAndOutlierMarkersAtom, []);
});

const useDcaFormData = () => {
  const fallbackAtom = useMemo(
    () =>
      atom({
        value: null,
        isValid: true,
        isDirty: false,
        error: null,
      }),
    []
  );
  const forecastSettingsForm = useAtomValue(forecastSettingsFormAtom);
  const oilModelForm = useAtomValue(oilModelFormAtom);
  const gasModelForm = useAtomValue(gasModelFormAtom);
  const waterModelForm = useAtomValue(waterModelFormAtom);

  const oilRampupModel = useAtomValue(oilModelForm.rampupModelAtomField);
  const gasRampupModel = useAtomValue(gasModelForm.rampupModelAtomField);
  const waterRampupModel = useAtomValue(waterModelForm.rampupModelAtomField);

  const oilRampupDuration = useAtomValue(
    oilModelForm.rampupDurationAtomField ?? fallbackAtom
  );
  const gasRampupDuration = useAtomValue(
    gasModelForm.rampupDurationAtomField ?? fallbackAtom
  );
  const waterRampupDuration = useAtomValue(
    waterModelForm.rampupDurationAtomField ?? fallbackAtom
  );

  const oilDeclineModel = useAtomValue(oilModelForm.declineModelAtomField);
  const gasDeclineModel = useAtomValue(gasModelForm.declineModelAtomField);
  const waterDeclineModel = useAtomValue(waterModelForm.declineModelAtomField);

  const isOilModelValid = useAtomValue(isOilModelValidAtom);
  const isGasModelValid = useAtomValue(isGasModelValidAtom);

  return {
    forecastSettingsForm,
    oilModelForm,
    gasModelForm,
    waterModelForm,
    oilRampupModel,
    gasRampupModel,
    waterRampupModel,
    oilRampupDuration,
    gasRampupDuration,
    waterRampupDuration,
    oilDeclineModel,
    gasDeclineModel,
    waterDeclineModel,
    isOilModelValid,
    isGasModelValid,
  };
};

export interface SelectedRecordVersion extends WellVersionsResponse {
  id: string;
  name: string;
}

const selectedRecordVersionAtom = atom<SelectedRecordVersion | null>(null);

/** Where the DCA editor reads production from; its versions are listed in recordVersionsAtom. */
const selectedProductionSourceAtom = atom<ProductionDataSource>('ihs');

/** The production a fit read: its source, and the id of the version it read. */
export interface FittedProduction {
  source: ProductionDataSource;
  /**
   * The version id Save stores, worked out when the fit ran (see
   * savedProductionVersionId). Null when no version was selected.
   */
  productionVolumeVersionId: string | null;
}

/**
 * The production the last successful Fit read. Save stores this source and
 * version, so a forecast keeps the production it was fitted on even if the
 * source or version was changed after the fit. Null before any fit.
 */
const fittedProductionAtom = atom<FittedProduction | null>(null);

const recordVersionsAtom = createMutationAtom<
  WellRecordVersionsResponse,
  'wellVersions'
>();

const resetSelectedRecordVersionAtom = atom(null, (get, set) => {
  const result = get(recordVersionsAtom).result;
  if (result?.type !== 'success') return;
  const latestVersion = currentOrFirstVersion(result.data.results);
  if (latestVersion) {
    set(selectedRecordVersionAtom, {
      ...latestVersion,
      id: latestVersion.versionId,
      name: latestVersion.versionName,
    });
  }
});

const selectedDcaScenarioAtom = atom<DcaScenariosResponse | null>(null);

const dcaScenariosAtom = createMutationAtom<
  WellDcaScenariosResponse,
  'dcaScenarios'
>();

const resetSelectedDcaScenarioAtom = atom(null, (get, set) => {
  const result = get(dcaScenariosAtom).result;
  if (result?.type !== 'success') return;

  const scenario = defaultDcaScenario(result.data.results);
  if (scenario) set(selectedDcaScenarioAtom, scenario);
});

const ALL_STREAMS: MineralType[] = ['oil', 'gas', 'water'];

const selectedFitStreamsAtom = atom<MineralType[]>(ALL_STREAMS);

const resetSelectedFitStreamsAtom = atom(null, (get, set) => {
  set(selectedFitStreamsAtom, ALL_STREAMS);
});

const selectedTypeCurveAtom = atom<TypeCurveOption | null>(null);

const typeCurveOptionsAtom = createMutationAtom<
  TypeCurveOptionsResponse,
  'typeCurveHeaders'
>();

const resetSelectedTypeCurveAtom = atom(null, (get, set, uwi: string) => {
  const typeCurveOptions = get(typeCurveOptionsAtom).result;

  if (typeCurveOptions?.type !== 'success') return;

  const preselectedOption = typeCurveOptions.data.results[uwi].find(
    (option) => option.isPreselected
  );
  if (preselectedOption) {
    set(selectedTypeCurveAtom, preselectedOption);
  } else {
    set(selectedTypeCurveAtom, null);
  }
});
const saveDcaForecasVersionAtom = createMutationAtom<
  SaveDcaForecastResponse,
  'saveDcaForecastVersion'
>();

const typeCurveVolumesDataAtom = createMutationAtom<
  BulkTypeCurveVolumesResponse,
  'bulkTypeCurveVolumes'
>();

const earlyTimeStreamsAtom = createMutationAtom<
  EarlyTimeStreamsResponse,
  'earlyTimeStreams'
>();

const applyPdpForecastLegacyMutationAtom = createMutationAtom<
  BulkForecastMetricData,
  'bulkForecastMetric'
>();

const applyPdpForecastInsightsLoadingAtom = atom<boolean>(false);

const pdpVersionsRequestParamsAtom = atom<PdpVersionsRequestParams>(null);

const pdpVersionsVolumesMutationAtom = createMutationAtom<
  BulkForecastMetricData,
  'bulkForecastMetric'
>();

const pdpVersionsForecastDataAtom = atom<ForecastData[]>([]);

const applyPdpForecastInsightsAtom = atom(
  (get) => get(applyPdpForecastInsightsLoadingAtom),
  async (
    _get,
    set,
    payload: {
      versionId: string;
      onSuccess: (data: unknown) => void;
      onError: (message: string) => void;
    }
  ) => {
    set(applyPdpForecastInsightsLoadingAtom, true);
    try {
      const response = await request.get<unknown>(
        `/wells/dca_forecasts/${payload.versionId}`
      );
      if (response && typeof response === 'object' && 'error' in response) {
        payload.onError(
          (response as { error?: string }).error ??
            'Failed to load PDP forecast parameters.'
        );
      } else {
        payload.onSuccess(response);
      }
    } catch {
      payload.onError('Failed to load PDP forecast parameters.');
    } finally {
      set(applyPdpForecastInsightsLoadingAtom, false);
    }
  }
);

const sidetrackVisibilityAtom = atomWithStorage<Record<string, boolean>>(
  'well-sidetrack-visibility',
  {}
);

// Atoms for controlling layer visibility (persisted to localStorage)
const isProducingVisibleAtom = atomWithStorage('well-producing-visible', true);
const isUnitsVisibleAtom = atomWithStorage('well-units-visible', true);

// Atom to trigger refetch of insight forecast versions after DCA forecast save
const refetchInsightForecastVersionsTriggerAtom = atom<number>(0);
const triggerRefetchInsightForecastVersionsAtom = atom(null, (get, set) => {
  set(
    refetchInsightForecastVersionsTriggerAtom,
    get(refetchInsightForecastVersionsTriggerAtom) + 1
  );
});

const totalInsightForecastVersionsAtom = atom<number>(0);

interface DcaProductionStreamConfigsAtomType {
  data: {
    productionStreamConfigs: ProductionStreamConfig[];
    forecastSettings: {
      durationPeriod: number;
      durationOption: DurationOption;
      qiFormat: QiFormat;
      diFormat: DiFormat;
    };
  } | null;
  loading: boolean;
  error: string | null;
}

const DcaProductionStreamConfigsAtomBase =
  atom<DcaProductionStreamConfigsAtomType>({
    data: null,
    loading: false,
    error: null,
  });

const dcaProductionStreamConfigsAtom = atom(
  (get) => get(DcaProductionStreamConfigsAtomBase),
  async (get, set, payload: { scenarioId: number; uwi: string }) => {
    set(DcaProductionStreamConfigsAtomBase, {
      data: null,
      loading: true,
      error: null,
    });

    try {
      const dcaScenarioResponse = await request.get<DcaScenarioResponse>(
        `/dca_scenarios/${payload.scenarioId}`,
        new AbortController()
      );

      if ('error' in dcaScenarioResponse)
        throw new Error('Error fetching DCA scenario');

      const areaConfigUrl = dcaScenarioResponse.resources?.areaConfigs?.href;

      if (!areaConfigUrl) return;

      const areaConfigResponse = await request.get<{
        results: AreaConfigResponse[];
        totalCount: number;
      }>(`${areaConfigUrl}?uwi=${payload.uwi}`, new AbortController());

      if ('error' in areaConfigResponse)
        throw new Error('Error fetching area config');

      const productionStreamConfigsUrl =
        areaConfigResponse.results[0].resources?.productionStreamConfigs?.href;

      if (!productionStreamConfigsUrl) return;

      const productionStreamConfigsResponse = await request.get<{
        results: ProductionStreamConfig[];
        totalCount: number;
      }>(productionStreamConfigsUrl, new AbortController());
      if ('error' in productionStreamConfigsResponse)
        throw new Error('Error fetching production stream configs');

      set(DcaProductionStreamConfigsAtomBase, {
        data: {
          productionStreamConfigs: productionStreamConfigsResponse.results,

          forecastSettings: {
            durationPeriod: dcaScenarioResponse?.durationPeriod,
            durationOption: dcaScenarioResponse?.durationOption,
            qiFormat: dcaScenarioResponse?.qiFormat,
            diFormat: dcaScenarioResponse?.diFormat,
          },
        },
        loading: false,
        error: null,
      });
    } catch (error) {
      set(DcaProductionStreamConfigsAtomBase, {
        data: null,
        loading: false,
        error:
          error instanceof Error ? error.message : 'Error creating scenario',
      });
    }
  }
);

const updateModelFormWithBulkForecastResults = (
  get: Getter,
  set: Setter,
  formData: ProductionDecline,
  modelFormAtom:
    | typeof oilModelFormAtom
    | typeof gasModelFormAtom
    | typeof waterModelFormAtom
) => {
  const currentForm = get(modelFormAtom);

  const formatDecimal = (value: number) => {
    return Number(
      formatNumber({
        value,
        minPrecision: 0,
        maxPrecision: 2,
        includeCommas: false,
      })
    );
  };

  const newValues = {
    declineModel:
      formData.declineModel ?? get(currentForm.declineModelAtomField).value,
    rampupModel: formData.rampUpModel ?? 'none',
    forecastStartDate: formData.declineStartDate
      ? parseMomentUtc(formData.declineStartDate).toDate()
      : get(currentForm.startDateAtomField).value,
    qi: formData.qi
      ? Math.round(formData.qi)
      : get(currentForm.qiAtomField).value,
    di: formData.di
      ? formatDecimal(formData.di)
      : get(currentForm.diAtomField).value,
    bFactor: formData.bFactor
      ? formatDecimal(formData.bFactor)
      : currentForm.bFactorAtomField
        ? get(currentForm.bFactorAtomField).value
        : null,
    dlim: formData.dLim
      ? formatDecimal(formData.dLim)
      : currentForm.dlimAtomField
        ? get(currentForm.dlimAtomField).value
        : null,
    qiRampup: formData.rampUpRate
      ? Math.round(formData.rampUpRate)
      : currentForm.qiRampupAtomField
        ? get(currentForm.qiRampupAtomField).value
        : null,
    rampupDuration: formData.rampUpMonths
      ? formData.rampUpMonths
      : currentForm.rampupDurationAtomField
        ? get(currentForm.rampupDurationAtomField).value
        : null,
    n: formData.rampUpSlope
      ? formatDecimal(formData.rampUpSlope)
      : currentForm.nAtomField
        ? get(currentForm.nAtomField).value
        : null,
    ri: formData.ri
      ? formatDecimal(formData.ri)
      : currentForm.riAtomField
        ? get(currentForm.riAtomField).value
        : null,
    tElf: formData.tElf
      ? formatDecimal(formData.tElf)
      : currentForm.tElfAtomField
        ? get(currentForm.tElfAtomField).value
        : null,
    m: formData.m
      ? formatDecimal(formData.m)
      : currentForm.mAtomField
        ? get(currentForm.mAtomField).value
        : null,
  };

  set(modelFormAtom, newValues);
};

interface ApplyPdpForecastPayload {
  uwi: string;
  versionId: string;
  source: 'Legacy' | 'Insights';
  declineType?: 'PDP' | 'TC';
}

interface ApplyPdpForecastAtomType {
  loading: boolean;
  error: string | null;
}

const applyPdpForecastAtomBase = atom<ApplyPdpForecastAtomType>({
  loading: false,
  error: null,
});

/** Cancels the PDP forecast apply in flight. */
let applyPdpForecastController = new AbortController();

const applyPdpForecastAtom = atom(
  (get) => get(applyPdpForecastAtomBase),
  async (get, set, payload: ApplyPdpForecastPayload) => {
    const isLegacy = payload.source === 'Legacy';
    const data = {
      query: {
        bulkForecasts: {
          convertLegacyParamsForDca: true,
          declineType: payload.declineType,
          ...(isLegacy
            ? {
                legacyForecasts: [
                  {
                    wells: [{ uwi: payload.uwi }],
                    versionIds: [payload.versionId],
                  },
                ],
              }
            : {
                wellDcaForecasts: {
                  wellDcaForecastIds: [payload.versionId],
                },
              }),
        },
      },
    };

    set(clearDcaNotificationGroupAtom, 'apply-pdp-forecast-error');

    set(applyPdpForecastAtomBase, {
      loading: true,
      error: null,
    });

    // Only the latest apply fills the forms. Applying again cancels the one
    // before, which would otherwise fill them with its forecast if it finished
    // last.
    applyPdpForecastController.abort();
    const controller = new AbortController();
    applyPdpForecastController = controller;

    try {
      const bulkForecastResponse = await request.post<
        BulkForecastMetricData,
        FailedApiResponse
      >(`/queries/reservoir/bulk_forecasts`, data, controller);

      // A later apply, or a cancel, took over, and it sets the loading state.
      if (controller.signal.aborted) return;

      if (bulkForecastResponse.type === 'error') {
        const error = bulkForecastResponse.data?.errors[0];

        const { details } = error || {};

        let msg = error?.message || '';

        if (details && typeof details === 'object') {
          // Convert details object into a readable string
          msg = Object.entries(details)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n');
        }

        throw new Error(msg);
      } else if (bulkForecastResponse.type !== 'success') {
        throw new Error('Failed to load PDP forecast parameters.');
      } else {
        // The response is keyed by well, and a well the service found nothing
        // for is left out of it rather than given an empty entry. Reading the
        // first element straight off the lookup turns that into an unreadable
        // TypeError, so say which well came back empty instead.
        const data = bulkForecastResponse.data.results[payload.uwi]?.[0];

        if (!data) {
          throw new Error(
            `No forecast parameters came back for well ${payload.uwi}.`
          );
        }

        if (data.oilDeclineParams) {
          const declineStartDate =
            data.oilDeclineParams.declineStartDate ||
            data.declineStartDate ||
            null;
          updateModelFormWithBulkForecastResults(
            get,
            set,
            { ...data.oilDeclineParams, declineStartDate },
            oilModelFormAtom
          );
        }

        if (data.gasDeclineParams) {
          const declineStartDate =
            data.gasDeclineParams.declineStartDate ||
            data.declineStartDate ||
            null;
          updateModelFormWithBulkForecastResults(
            get,
            set,
            { ...data.gasDeclineParams, declineStartDate },
            gasModelFormAtom
          );
        }

        if (data.waterDeclineParams) {
          const declineStartDate =
            data.waterDeclineParams.declineStartDate ||
            data.declineStartDate ||
            null;
          updateModelFormWithBulkForecastResults(
            get,
            set,
            { ...data.waterDeclineParams, declineStartDate },
            waterModelFormAtom
          );
        }

        set(_lockParamsStateAtom, initialLockedParams);
      }
      // The forms now hold the PDP forecast's parameters, not a fit's, so Save
      // uses the production selected now.
      set(fittedProductionAtom, null);
      set(applyPdpForecastAtomBase, {
        loading: false,
        error: null,
      });
      set(regressionDataAtom, {
        ...get(regressionDataAtom),
        refetchEvalVolumes: true,
      });
    } catch (error) {
      set(addDcaNotificationAtom, 'apply-pdp-forecast-error', [
        {
          id: 'apply-pdp-forecast-error',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to load PDP forecast parameters.',
          title: 'Error',
          type: 'error',
        },
      ]);
      set(applyPdpForecastAtomBase, {
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to load PDP forecast parameters.',
      });
    }
  }
);

/**
 * Cancels the PDP forecast apply in flight, so it does not fill the forms when
 * it finishes, and clears its loading state.
 */
const cancelApplyPdpForecastAtom = atom(null, (_get, set) => {
  applyPdpForecastController.abort();
  set(applyPdpForecastAtomBase, { loading: false, error: null });
});

const defaultModelFormValues = {
  declineModel: 'modified_hyperbolic' as const,
  rampupModel: 'none' as const,
  forecastStartDate: null,
  qi: null,
  di: null,
  bFactor: null,
  dlim: null,
  qiRampup: null,
  rampupDuration: null,
  n: null,
  ri: null,
  tElf: null,
  m: null,
};

const resetDcaModalStateAtom = atom(null, (get, set) => {
  // Reset regression data and segmentation/outlier markers
  set(resetRegressionDataAtom);

  // Reset model forms to defaults
  set(oilModelFormAtom, defaultModelFormValues);
  set(gasModelFormAtom, defaultModelFormValues);
  set(waterModelFormAtom, defaultModelFormValues);

  // Reset forecast settings to defaults
  set(resetForecastSettingsFormAtom);

  // Reset locked parameters
  set(_lockParamsStateAtom, initialLockedParams);

  // Clear all DCA notifications
  set(clearAllDcaNotificationsAtom);

  // Reset initial model values
  set(initialOilModelValuesAtom, null);
  set(initialGasModelValuesAtom, null);
  set(initialWaterModelValuesAtom, null);

  // Reset selections
  set(selectedRecordVersionAtom, null);
  set(fittedProductionAtom, null);
  set(selectedFitStreamsAtom, ALL_STREAMS);
  set(selectedTypeCurveAtom, null);
  set(convertToHyperbolicErrorAtom, []);
  set(pdpVersionsRequestParamsAtom, null);
  set(pdpVersionsForecastDataAtom, []);
});

export {
  useProductionRecordsVersionsAtom,
  useProductionForecastsVersionsAtom,
  useSupportingFilesAtom,
  useWellAtom,
  useWellFeaturesAtom,
  useWellFormationAtom,
  useUnitTableAtom,
  useWellVersionsAtom,
  wellboresMutationAtom,
  wellboreGeometryExportMutationAtom,
  wellMapMutationAtom,
  wellSpacingRequestMutationAtom,
  wellSpacingResultsMutationAtom,
  wellSpacingEvalPointResultsMutationAtom,
  wellLandingAnalysisRequestMutationAtom,
  wellLandingAnalysisPreviewCacheAtom,
  cancelLandingAnalysisRequestMutationAtom,
  savedSubjectUwi14sAtom,
  landingSettingsDirtyAtom,
  landingSettingsFormValidAtom,
  landingAnalysisSettingsAtom,
  landingAnalysisDraftSettingsAtom,
  wellPerforationFactorMutationAtom,
  wellStickLateralFactorMutationAtom,
  wellScopedUnitLayerVisibility,
  resetForecastSettingsFormAtom,
  resetModelFormAtom,
  resetOilModelFormAtom,
  resetGasModelFormAtom,
  resetWaterModelFormAtom,
  oilModelFormAtom,
  gasModelFormAtom,
  waterModelFormAtom,
  forecastSettingsFormAtom,
  isOilModelValidAtom,
  isGasModelValidAtom,
  isWaterModelValidAtom,
  isModelFormDirtyAtom,
  resetModelFormDirtyAtom,
  oilModelFormValuesAtom,
  gasModelFormValuesAtom,
  waterModelFormValuesAtom,
  primaryDCAModelAtom,
  isMainDCAModelNonExponentialAtom,
  convertToHyperbolicErrorAtom,
  isPrimaryDCAModelValidAtom,
  lockParamsAtom,
  regressionDataAtom,
  fetchRegressionAtom,
  useDcaFormData,
  selectedRecordVersionAtom,
  selectedProductionSourceAtom,
  fittedProductionAtom,
  recordVersionsAtom,
  selectedDcaScenarioAtom,
  dcaScenariosAtom,
  selectedFitStreamsAtom,
  resetSelectedRecordVersionAtom,
  resetSelectedDcaScenarioAtom,
  resetSelectedFitStreamsAtom,
  saveDcaForecasVersionAtom,
  segmentationAndOutlierMarkersAtom,
  initialOilModelValuesAtom,
  initialGasModelValuesAtom,
  initialWaterModelValuesAtom,
  selectedTypeCurveAtom,
  typeCurveOptionsAtom,
  resetSelectedTypeCurveAtom,
  dcaNotificationAtom,
  addDcaNotificationAtom,
  setDcaNotificationAtom,
  clearDcaNotificationGroupAtom,
  clearAllDcaNotificationsAtom,
  typeCurveVolumesDataAtom,
  earlyTimeStreamsAtom,
  applyPdpForecastLegacyMutationAtom,
  applyPdpForecastInsightsAtom,
  applyPdpForecastInsightsLoadingAtom,
  pdpVersionsRequestParamsAtom,
  pdpVersionsVolumesMutationAtom,
  pdpVersionsForecastDataAtom,
  sidetrackVisibilityAtom,
  isProducingVisibleAtom,
  isUnitsVisibleAtom,
  resetRegressionDataAtom,
  refetchInsightForecastVersionsTriggerAtom,
  triggerRefetchInsightForecastVersionsAtom,
  totalInsightForecastVersionsAtom,
  dcaProductionStreamConfigsAtom,
  colorByAttributeAtom,
  applyPdpForecastAtom,
  cancelApplyPdpForecastAtom,
  defaultModelFormValues,
  resetDcaModalStateAtom,
};
